// ═══════════════════════════════════════════════════════════════
// Global Search — incidents + module directory
// Always-available data, no lazy store dependency
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { IconSearch, IconX, IconAlertTriangle, IconLayoutDashboard } from '@tabler/icons-react';
import { useAuthStore } from '../../auth/auth-store';
import { MODULES } from '../../modules';
import type { Incident } from '../../auth/auth-store';

interface Props {
  opened: boolean;
  onClose: () => void;
}

interface Result {
  id: string;
  kind: 'incident' | 'module';
  label: string;
  detail: string;
  route: string;
  dotColor?: string;
}

function incidentDot(status: string) {
  if (status === 'active') return '#D33423';
  if (status === 'monitoring') return '#d97706';
  return '#939598';
}

export function GlobalSearch({ opened, onClose }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const incidents = useAuthStore(s => s.incidents);

  // Reset query on close; focus input on open
  useEffect(() => {
    if (!opened) { setQuery(''); return; }
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [opened]);

  // Close on Escape
  useEffect(() => {
    if (!opened) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [opened, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (!opened) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [opened]);

  const results = useMemo<Result[]>(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 1) return [];
    const hits: Result[] = [];

    // Incidents
    for (const inc of incidents as Incident[]) {
      if (hits.length >= 12) break;
      if (
        (inc.name || '').toLowerCase().includes(q) ||
        (inc.location || '').toLowerCase().includes(q) ||
        (inc.commander || '').toLowerCase().includes(q) ||
        (inc.incidentType || '').toLowerCase().includes(q)
      ) {
        hits.push({
          id: `inc-${inc.id}`,
          kind: 'incident',
          label: inc.name,
          detail: [inc.location, inc.commander ? `IC: ${inc.commander}` : null, inc.status?.toUpperCase()]
            .filter(Boolean).join(' · '),
          route: `/incidents/${inc.id}`,
          dotColor: incidentDot(inc.status || ''),
        });
      }
    }

    // Modules
    for (const mod of MODULES) {
      if (hits.length >= 16) break;
      if (
        mod.name.toLowerCase().includes(q) ||
        mod.description.toLowerCase().includes(q) ||
        mod.phase.toLowerCase().includes(q)
      ) {
        hits.push({
          id: `mod-${mod.id}`,
          kind: 'module',
          label: mod.name,
          detail: mod.description,
          route: mod.route,
        });
      }
    }

    return hits;
  }, [query, incidents]);

  const pick = useCallback((route: string) => {
    navigate(route);
    onClose();
  }, [navigate, onClose]);

  if (!opened) return null;

  return createPortal(
    <div className="gs-backdrop" onClick={onClose}>
      <div className="gs-panel" onClick={e => e.stopPropagation()} role="dialog" aria-label="Global search">

        {/* Search input */}
        <div className="gs-input-row">
          <IconSearch size={16} className="gs-icon" />
          <input
            ref={inputRef}
            className="gs-input"
            placeholder="Search incidents, modules…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <button className="gs-close" onClick={onClose} aria-label="Close search">
            <IconX size={14} />
          </button>
        </div>

        {/* Results */}
        <div className="gs-results">
          {query.length === 0 && (
            <div className="gs-hint">Type to search incidents and modules</div>
          )}

          {query.length > 0 && results.length === 0 && (
            <div className="gs-empty">
              No results for "<strong>{query}</strong>"
            </div>
          )}

          {results.length > 0 && (
            <>
              {/* Group: incidents */}
              {results.some(r => r.kind === 'incident') && (
                <div className="gs-group">
                  <div className="gs-group-label">Incidents</div>
                  {results.filter(r => r.kind === 'incident').map(r => (
                    <button key={r.id} className="gs-item" onClick={() => pick(r.route)}>
                      <span className="gs-dot" style={{ background: r.dotColor }} />
                      <span className="gs-item-text">
                        <span className="gs-item-label">{r.label}</span>
                        {r.detail && <span className="gs-item-detail">{r.detail}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Group: modules */}
              {results.some(r => r.kind === 'module') && (
                <div className="gs-group">
                  <div className="gs-group-label">Modules</div>
                  {results.filter(r => r.kind === 'module').map(r => (
                    <button key={r.id} className="gs-item" onClick={() => pick(r.route)}>
                      <span className="gs-module-icon"><IconLayoutDashboard size={13} /></span>
                      <span className="gs-item-text">
                        <span className="gs-item-label">{r.label}</span>
                        {r.detail && <span className="gs-item-detail">{r.detail}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="gs-footer">
          <kbd className="gs-kbd">↵</kbd> to open &nbsp;·&nbsp; <kbd className="gs-kbd">Esc</kbd> to close
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default GlobalSearch;
